import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import VisibilityIcon from '@mui/icons-material/Visibility';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import AuthContext from '../../context/authContext';
import EditableList from '../EditableList/EditableList';
import useAiProposal from '../../hooks/useAiProposal';
import AiProposalDetailDialog from '../AiProposal/AiProposalDetailDialog';
import { getAiProposalStatusLabel } from '../AiProposal/aiProposalStatus';
import {
  getStatisticsKpiLabelColor,
  getStatisticsSurfaceBackground,
  viewRootTextPrimarySx,
} from '../../util/chartTheme';

const RESOURCE_TYPE = 'aiProposal';
const DEFAULT_LIST_PARAMS = { limit: 50 };
const TOP_DOMAIN_COUNT = 5;

function formatDateTime(value) {
  if (!value) return '—';
  const d = dayjs(value);
  return d.isValid() ? d.format('DD.MM.YYYY HH:mm') : '—';
}

// Tells the EditableList to reload the proposals from the server
function refreshProposalList() {
  window.dispatchEvent(
    new CustomEvent('editableListRefresh', { detail: { resource: RESOURCE_TYPE } })
  );
}

function AiAuditView() {
  const theme = useTheme();
  const authCtx = useContext(AuthContext);
  const { getAiProposalById, approveAiProposal, rejectAiProposal, getAiProposalStats } =
    useAiProposal();

  const [statusFilter, setStatusFilter] = useState('');
  const [listParams, setListParams] = useState(DEFAULT_LIST_PARAMS);
  const [detailDialog, setDetailDialog] = useState({ open: false, proposal: null });
  const [detailLoading, setDetailLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const domainInputRef = useRef(null);
  const serviceAccountInputRef = useRef(null);
  const fromInputRef = useRef(null);
  const toInputRef = useRef(null);
  const getAiProposalStatsRef = useRef(getAiProposalStats);
  getAiProposalStatsRef.current = getAiProposalStats;

  const canRead = authCtx.checkRight('aiProposal:read');
  const canApprove = authCtx.checkRight('aiProposal:approve');
  const canReject = authCtx.checkRight('aiProposal:reject');

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const response = await getAiProposalStatsRef.current();
      setStats(response?.stats || null);
    } catch {
      // Toast is shown inside the hook
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!canRead || !authCtx.token) return;
    loadStats();
  }, [canRead, authCtx.token, loadStats]);

  const applyFilters = useCallback(() => {
    const domainValue = domainInputRef.current?.value?.trim() || '';
    const serviceAccountValue = serviceAccountInputRef.current?.value?.trim() || '';
    const fromValue = fromInputRef.current?.value || '';
    const toValue = toInputRef.current?.value || '';

    const nextParams = { limit: 50 };
    if (statusFilter) {
      nextParams.status = statusFilter;
    }
    if (domainValue) {
      nextParams.targetDomain = domainValue;
    }
    if (serviceAccountValue) {
      nextParams.serviceAccountId = serviceAccountValue;
    }
    if (fromValue) {
      nextParams.from = new Date(`${fromValue}T00:00:00`).toISOString();
    }
    if (toValue) {
      nextParams.to = new Date(`${toValue}T23:59:59`).toISOString();
    }
    setListParams(nextParams);
  }, [statusFilter]);

  const resetFilters = useCallback(() => {
    if (domainInputRef.current) domainInputRef.current.value = '';
    if (serviceAccountInputRef.current) serviceAccountInputRef.current.value = '';
    if (fromInputRef.current) fromInputRef.current.value = '';
    if (toInputRef.current) toInputRef.current.value = '';
    setStatusFilter('');
    setListParams(DEFAULT_LIST_PARAMS);
  }, []);

  const openDetails = useCallback(
    async item => {
      if (!item?._id) return;
      setDetailDialog({ open: true, proposal: null });
      setDetailLoading(true);
      try {
        const response = await getAiProposalById(item._id);
        setDetailDialog({ open: true, proposal: response?.aiProposal || null });
      } catch {
        // Toast is shown inside the hook
        setDetailDialog({ open: false, proposal: null });
      } finally {
        setDetailLoading(false);
      }
    },
    [getAiProposalById]
  );

  const closeDetails = useCallback(() => {
    setDetailDialog({ open: false, proposal: null });
  }, []);

  // Executes the approve/reject call, shows the outcome toast and refreshes list and stats
  const handleDecision = useCallback(
    async (proposal, mode, note) => {
      if (!proposal?._id) return;
      const response =
        mode === 'approve'
          ? await approveAiProposal(proposal._id, note)
          : await rejectAiProposal(proposal._id, note);

      closeDetails();

      // null means the request was stored for step-up authentication
      if (response === null) return;

      if (mode === 'approve') {
        const updated = response?.aiProposal;
        if (updated?.status === 'failed') {
          toast.warning(
            'Vorschlag angenommen, aber Anwendung fehlgeschlagen: ' +
              (updated?.applyResult?.error || 'Unbekannter Fehler')
          );
        } else {
          toast.success('Vorschlag angenommen und angewendet');
        }
      } else {
        toast.success('Vorschlag abgelehnt');
      }
      refreshProposalList();
      loadStats();
    },
    [approveAiProposal, rejectAiProposal, closeDetails, loadStats]
  );

  const statSurfaceBg = useMemo(() => getStatisticsSurfaceBackground(theme), [theme]);
  const kpiLabelColor = useMemo(() => getStatisticsKpiLabelColor(theme), [theme]);

  const statCards = useMemo(() => {
    const byStatus = stats?.byStatus || {};
    return [
      { label: 'Gesamt', value: stats?.total ?? 0 },
      { label: 'Ausstehend', value: byStatus.pending ?? 0 },
      { label: 'Angewendet', value: byStatus.applied ?? 0 },
      { label: 'Abgelehnt', value: byStatus.rejected ?? 0 },
      { label: 'Fehlgeschlagen', value: byStatus.failed ?? 0 },
    ];
  }, [stats]);

  const topDomains = useMemo(() => {
    const byDomain = Array.isArray(stats?.byDomain) ? stats.byDomain : [];
    return byDomain.slice(0, TOP_DOMAIN_COUNT);
  }, [stats]);

  const aiAuditSchema = useMemo(
    () => ({
      createdAt: {
        label: 'Erstellt',
        api: { itemField: 'createdAt' },
        type: 'text',
        readOnly: true,
        minWidth: '150px',
        listDisplayValue: (value, item) => formatDateTime(item?.meta?.createdAt),
      },
      serviceAccountName: {
        label: 'Service-Account',
        api: { itemField: 'serviceAccountName' },
        type: 'text',
        readOnly: true,
        minWidth: '170px',
      },
      targetDomain: {
        label: 'Domain',
        api: { itemField: 'targetDomain' },
        type: 'text',
        readOnly: true,
        minWidth: '140px',
      },
      method: {
        label: 'Methode',
        api: { itemField: 'method' },
        type: 'text',
        readOnly: true,
        minWidth: '100px',
        listDisplayValue: (value, item) => item?.action?.method || '—',
      },
      summary: {
        label: 'Zusammenfassung',
        api: { itemField: 'summary' },
        type: 'text',
        readOnly: true,
        minWidth: '280px',
      },
      status: {
        label: 'Status',
        api: { itemField: 'status' },
        type: 'text',
        readOnly: true,
        minWidth: '130px',
        listDisplayValue: value => getAiProposalStatusLabel(value),
      },
      decidedByName: {
        label: 'Entschieden von',
        api: { itemField: 'decidedByName' },
        type: 'text',
        readOnly: true,
        minWidth: '160px',
        listDisplayValue: (value, item) => item?.decision?.decidedByName || '—',
      },
      decidedAt: {
        label: 'Entschieden am',
        api: { itemField: 'decidedAt' },
        type: 'text',
        readOnly: true,
        minWidth: '150px',
        listDisplayValue: (value, item) => formatDateTime(item?.decision?.decidedAt),
      },
    }),
    []
  );

  if (!canRead) {
    return <Navigate to='/welcome' replace />;
  }

  return (
    <Box sx={{ ...viewRootTextPrimarySx, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 1.5,
        }}
      >
        {statCards.map(card => (
          <Box
            key={card.label}
            sx={{
              bgcolor: statSurfaceBg,
              borderRadius: 2,
              p: 2,
              border: 1,
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
            }}
          >
            <Typography variant='body2' sx={{ color: kpiLabelColor, fontSize: '0.85rem' }}>
              {card.label}
            </Typography>
            <Typography component='strong' sx={{ fontSize: '1.5rem', fontWeight: 700 }}>
              {statsLoading && !stats ? '…' : card.value}
            </Typography>
          </Box>
        ))}
      </Box>

      {topDomains.length > 0 && (
        <Stack direction='row' spacing={1} alignItems='center' sx={{ flexWrap: 'wrap', gap: 0.5 }}>
          <Typography variant='caption' color='text.secondary'>
            Top-Domains:
          </Typography>
          {topDomains.map(entry => (
            <Chip key={entry.domain} size='small' label={`${entry.domain}: ${entry.count}`} />
          ))}
        </Stack>
      )}

      <EditableList
        title='KI-Audit'
        resourceType={RESOURCE_TYPE}
        schema={aiAuditSchema}
        searchPlaceholder='KI-Audit durchsuchen'
        parentIsEditable={false}
        options={{
          disableCreate: true,
          disableDelete: true,
          disableDetailView: true,
          fillHeight: false,
          listConfig: {
            endpoint: '/api/v1/aiProposal',
            resultsKey: 'aiProposals',
            pageParam: 'page',
            limitParam: 'limit',
          },
          // Server-side across the whole dataset: the backend GET /api/v1/aiProposal
          // implements the header `search`, the per-column `filters` JSON param and
          // `sortKey`/`sortDirection` for every displayed column, plus the dedicated
          // top-control params from listParams (status, targetDomain, serviceAccountId, ...).
          serverSideFiltering: true,
          serverSideSorting: true,
        }}
        listParams={listParams}
        renderTopControlsStart={() => (
          <Box
            sx={{
              flex: '1 1 100%',
              minWidth: 0,
              maxWidth: '100%',
            }}
          >
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <TextField
                  size='small'
                  select
                  fullWidth
                  label='Status'
                  value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value)}
                >
                  <MenuItem value=''>Alle</MenuItem>
                  <MenuItem value='pending'>Ausstehend</MenuItem>
                  <MenuItem value='applied'>Angewendet</MenuItem>
                  <MenuItem value='rejected'>Abgelehnt</MenuItem>
                  <MenuItem value='failed'>Fehlgeschlagen</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <TextField
                  size='small'
                  fullWidth
                  label='Domain'
                  inputRef={domainInputRef}
                  placeholder='z. B. product'
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      applyFilters();
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <TextField
                  size='small'
                  fullWidth
                  label='Service-Account-ID'
                  inputRef={serviceAccountInputRef}
                  placeholder='z. B. 665f1a2b3c4d5e6f7a8b9c0d'
                  helperText='Vollständige Service-Account-ID'
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      applyFilters();
                    }
                  }}
                />
              </Grid>
              <Grid item xs={6} sm={6} md={4} lg={2}>
                <TextField
                  size='small'
                  fullWidth
                  label='Von'
                  type='date'
                  inputRef={fromInputRef}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6} sm={6} md={4} lg={2}>
                <TextField
                  size='small'
                  fullWidth
                  label='Bis'
                  type='date'
                  inputRef={toInputRef}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </Box>
        )}
        renderTopControlsEnd={() => (
          <Stack
            direction='row'
            spacing={1}
            sx={{
              flex: { xs: '1 1 100%', sm: '0 0 auto' },
              minWidth: 0,
              justifyContent: { xs: 'stretch', sm: 'flex-start' },
              '& > .MuiButton-root': { xs: { flex: '1 1 auto' } },
            }}
          >
            <Button variant='contained' size='small' onClick={applyFilters}>
              Filtern
            </Button>
            <Button variant='outlined' size='small' onClick={resetFilters}>
              Zurücksetzen
            </Button>
          </Stack>
        )}
        renderRowActions={item => (
          <Tooltip title='Details'>
            <IconButton size='small' onClick={() => openDetails(item)}>
              <VisibilityIcon fontSize='small' />
            </IconButton>
          </Tooltip>
        )}
        visibleColumnKeys={[
          'createdAt',
          'serviceAccountName',
          'targetDomain',
          'method',
          'summary',
          'status',
          'decidedByName',
          'decidedAt',
        ]}
      />

      <AiProposalDetailDialog
        open={detailDialog.open}
        proposal={detailDialog.proposal}
        loading={detailLoading}
        onClose={closeDetails}
        onApprove={
          canApprove ? note => handleDecision(detailDialog.proposal, 'approve', note) : null
        }
        onReject={canReject ? note => handleDecision(detailDialog.proposal, 'reject', note) : null}
      />
    </Box>
  );
}

export default AiAuditView;
